import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Container, Button, Grid, List, ListItem, ListItemText, Divider, CircularProgress, Tabs, Tab, IconButton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { doc, getDoc, collection, query, where, getDocs, orderBy, limit, Timestamp, updateDoc } from 'firebase/firestore';
import { db } from '../firebase-config';
import DebugConversations from '../components/DebugConversations';
import ManageSubscriptionButton from '../components/ManageSubscriptionButton';
import SettingsIcon from '@mui/icons-material/Settings';
import NotificationsIcon from '@mui/icons-material/Notifications';
import HelpIcon from '@mui/icons-material/Help';
import RefreshIcon from '@mui/icons-material/Refresh';
import ChatIcon from '@mui/icons-material/Chat';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';

const Dashboard = () => {
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const [businessData, setBusinessData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(1); // Default to CONVERSATIONS tab
  const [missedCallCount, setMissedCallCount] = useState(0);
  const [messageCount, setMessageCount] = useState(0);
  const [responseRate, setResponseRate] = useState(0);
  const [revenue, setRevenue] = useState(0);
  const [jobValue, setJobValue] = useState(150); // Default job value

  // Fetch business data on component mount
  useEffect(() => {
    const fetchBusinessData = async () => {
      if (!currentUser) return;

      try {
        setLoading(true);
        const businessDoc = await getDoc(doc(db, 'businesses', currentUser.uid));

        if (businessDoc.exists()) {
          const data = businessDoc.data();
          setBusinessData(data);

          // Set job value from business data if available
          const storedJobValue = data.jobValue || 150;
          setJobValue(storedJobValue);

          // Fetch all missed calls for the account (for the last year)
          const oneYearAgo = new Date();
          oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

          // For the month-to-date counter
          const startOfMonth = new Date();
          startOfMonth.setDate(1);
          startOfMonth.setHours(0, 0, 0, 0);

          let allMissedCalls = 0;
          let monthToDateMissedCalls = 0;

          try {
            // Query all forwarded_calls for this business
            const allCallsQuery = query(
              collection(db, 'forwarded_calls'),
              where('businessId', '==', currentUser.uid)
            );

            const allCallsSnapshot = await getDocs(allCallsQuery);

            // Count all calls and month-to-date calls
            allCallsSnapshot.forEach(doc => {
              const data = doc.data();
              allMissedCalls++;

              // Also count month-to-date calls for the display
              if (data.timestamp) {
                const callTime = data.timestamp.toDate ?
                  data.timestamp.toDate() :
                  new Date(data.timestamp);
                if (callTime >= startOfMonth) {
                  monthToDateMissedCalls++;
                }
              }
            });

            // Set the month-to-date counter with actual value (can be 0)
            setMissedCallCount(monthToDateMissedCalls);

            console.log(`Found ${allMissedCalls} total missed calls and ${monthToDateMissedCalls} month-to-date`);
          console.log('Current user ID:', currentUser.uid);
          console.log('Query result snapshot size:', allCallsSnapshot.size);

          // Debug: Log all call documents
          allCallsSnapshot.forEach(doc => {
            console.log('Call document:', doc.id, doc.data());
          });
          } catch (error) {
            console.error('Error fetching missed calls:', error);
            setMissedCallCount(0); // Show 0 if query fails
            allMissedCalls = 0;
            monthToDateMissedCalls = 0;
          }

          // Calculate revenue based on all missed calls
          const estimatedRevenue = allMissedCalls * jobValue;
          console.log(`Calculating total revenue: ${allMissedCalls} total calls * $${jobValue}/job = $${estimatedRevenue}`);
          setRevenue(estimatedRevenue);

          // Count messages sent
          const messagesQuery = query(
            collection(db, 'businesses', currentUser.uid, 'conversations')
          );

          const conversationsSnapshot = await getDocs(messagesQuery);
          let totalMessages = 0;

          conversationsSnapshot.forEach(doc => {
            const convData = doc.data();
            if (convData.messages && Array.isArray(convData.messages)) {
              totalMessages += convData.messages.length;
            }
          });

          setMessageCount(totalMessages); // Show actual message count, can be 0

          console.log('Conversations query result size:', conversationsSnapshot.size);
          console.log('Total messages found:', totalMessages);

          // Debug: Log all conversation documents
          conversationsSnapshot.forEach(doc => {
            const convData = doc.data();
            console.log('Conversation document:', doc.id, {
              customerNumber: convData.customerNumber,
              messagesCount: convData.messages?.length || 0,
              status: convData.status,
              createdAt: convData.createdAt
            });
          });

          // Set response rate to 0 if there are no messages, otherwise 100%
          setResponseRate(totalMessages > 0 ? 100 : 0);

          // Debug: Check other collections for data
          try {
            // Check generic_sms_logs
            const smsLogsQuery = query(collection(db, 'generic_sms_logs'), limit(10));
            const smsLogsSnapshot = await getDocs(smsLogsQuery);
            console.log('Generic SMS logs count:', smsLogsSnapshot.size);

            // Check messages collection
            const messagesQuery = query(collection(db, 'messages'), limit(10));
            const messagesSnapshot = await getDocs(messagesQuery);
            console.log('Messages collection count:', messagesSnapshot.size);

            // Check sms_proxy_messages
            const proxyQuery = query(collection(db, 'sms_proxy_messages'), limit(10));
            const proxySnapshot = await getDocs(proxyQuery);
            console.log('SMS proxy messages count:', proxySnapshot.size);

          } catch (debugError) {
            console.error('Error checking other collections:', debugError);
          }

        } else {
          setError('No business information found.');
        }
      } catch (err) {
        console.error('Error fetching business data:', err);
        setError('Failed to load business information.');
      } finally {
        setLoading(false);
      }
    };

    fetchBusinessData();
  }, [currentUser]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out', error);
    }
  };

  const fetchMetrics = async () => {
    if (!currentUser) return;

    try {
      // For the month-to-date counter
      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);

      let allMissedCalls = 0;
      let monthToDateMissedCalls = 0;

      try {
        // Query all forwarded_calls for this business
        const allCallsQuery = query(
          collection(db, 'forwarded_calls'),
          where('businessId', '==', currentUser.uid)
        );

        const allCallsSnapshot = await getDocs(allCallsQuery);

        // Count all calls and month-to-date calls
        allCallsSnapshot.forEach(doc => {
          const data = doc.data();
          allMissedCalls++;

          // Also count month-to-date calls for the display
          if (data.timestamp) {
            const callTime = data.timestamp.toDate ?
              data.timestamp.toDate() :
              new Date(data.timestamp);
            if (callTime >= startOfMonth) {
              monthToDateMissedCalls++;
            }
          }
        });

        // Set the month-to-date counter with actual value
        setMissedCallCount(monthToDateMissedCalls);

        console.log(`Found ${allMissedCalls} total missed calls and ${monthToDateMissedCalls} month-to-date`);
      } catch (error) {
        console.error('Error fetching missed calls in fetchMetrics:', error);
        // Keep the current missedCallCount value
        allMissedCalls = missedCallCount; // Use current value as fallback
      }

      // Calculate revenue based on all missed calls
      const estimatedRevenue = allMissedCalls * jobValue;
      console.log(`Calculating total revenue in fetchMetrics: ${allMissedCalls} total calls * $${jobValue}/job = $${estimatedRevenue}`);
      setRevenue(estimatedRevenue);

      // Count messages sent
      const messagesQuery = query(
        collection(db, 'businesses', currentUser.uid, 'conversations')
      );

      const conversationsSnapshot = await getDocs(messagesQuery);
      let totalMessages = 0;

      conversationsSnapshot.forEach(doc => {
        const convData = doc.data();
        if (convData.messages && Array.isArray(convData.messages)) {
          totalMessages += convData.messages.length;
        }
      });

      setMessageCount(totalMessages); // Show actual message count

      // Set response rate to 0 if there are no messages, otherwise 100%
      setResponseRate(totalMessages > 0 ? 100 : 0);

    } catch (err) {
      console.error('Error fetching metrics:', err);
    }
  };

  const refreshConversations = () => {
    // Refresh metrics
    fetchMetrics();

    // This will trigger a re-render of the DebugConversations component
    setActiveTab(0);
    setTimeout(() => setActiveTab(1), 100);
  };

  return (
    <Container maxWidth="lg">
      {/* Header with icons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, mb: 2 }}>
        <IconButton onClick={() => navigate('/settings')} sx={{ color: '#1976d2' }}>
          <SettingsIcon />
        </IconButton>
        <IconButton sx={{ color: '#1976d2' }}>
          <NotificationsIcon />
        </IconButton>
        <IconButton onClick={() => navigate('/help')} sx={{ color: '#1976d2' }}>
          <HelpIcon />
        </IconButton>
        <Button
          variant="outlined"
          onClick={handleLogout}
          sx={{ ml: 2 }}
        >
          LOGOUT
        </Button>
      </Box>

      <Box sx={{ mt: 2, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Business Dashboard <Typography variant="caption" color="textSecondary" component="span">(v1.2)</Typography>
        </Typography>

        {error && (
          <Paper elevation={3} sx={{ p: 3, mb: 3, bgcolor: 'error.light' }}>
            <Typography variant="body1">{error}</Typography>
          </Paper>
        )}

        {/* Analytics Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                height: '100%',
                bgcolor: '#2196f3',
                color: 'white',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <Typography variant="h6" gutterBottom>
                Missed Calls
              </Typography>
              <Typography variant="h2" component="div" sx={{ flexGrow: 1 }}>
                {missedCallCount}
              </Typography>
              <Typography variant="body2">
                Month to date
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                height: '100%',
                bgcolor: '#4caf50',
                color: 'white',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <Typography variant="h6" gutterBottom>
                Auto-Texts
              </Typography>
              <Typography variant="h2" component="div" sx={{ flexGrow: 1 }}>
                {messageCount}
              </Typography>
              <Typography variant="body2">
                Messages sent
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                height: '100%',
                bgcolor: '#03a9f4',
                color: 'white',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <Typography variant="h6" gutterBottom>
                Response Rate
              </Typography>
              <Typography variant="h2" component="div" sx={{ flexGrow: 1 }}>
                {responseRate}%
              </Typography>
              <Typography variant="body2">
                Customer satisfaction
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                height: '100%',
                bgcolor: '#e91e63',
                color: 'white',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <Typography variant="h6" gutterBottom>
                Revenue Recovered
              </Typography>
              <Typography variant="h2" component="div" sx={{ flexGrow: 1 }}>
                ${revenue.toLocaleString()}
              </Typography>
              <Typography variant="body2">
                Based on all missed calls at ${jobValue}/job
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Revenue Configuration */}
        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Revenue Settings
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="body1">
              Average job value: $
            </Typography>
            <input
              type="number"
              value={jobValue}
              onChange={(e) => {
                const newValue = parseInt(e.target.value) || 0;
                setJobValue(newValue);

                // Refresh metrics to get the latest total missed call count
                fetchMetrics();

                // Save to business data in Firestore
                if (currentUser) {
                  try {
                    const businessRef = doc(db, 'businesses', currentUser.uid);
                    // Only update the jobValue field
                    updateDoc(businessRef, {
                      jobValue: newValue
                    }).then(() => {
                      console.log('Job value updated in Firestore');
                    }).catch(err => {
                      console.error('Error updating job value:', err);
                    });
                  } catch (err) {
                    console.error('Error updating job value:', err);
                  }
                }
              }}
              style={{
                padding: '8px',
                width: '100px',
                fontSize: '16px',
                border: '1px solid #ccc',
                borderRadius: '4px'
              }}
            />
            <Typography variant="body2" color="textSecondary">
              Used to calculate total potential revenue recovered from all missed calls
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 2 }}>
            Tip: Set this to your average job value to see how much revenue you could recover by responding to all missed calls. The calculation is based on the total number of missed calls for your account.
          </Typography>
        </Paper>

        {/* Quick Actions Section */}
        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Quick Actions
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<ChatIcon />}
              onClick={() => navigate('/test')}
            >
              TEST THE AI
            </Button>

            <Button
              variant="outlined"
              color="primary"
              startIcon={<QuestionAnswerIcon />}
              onClick={() => navigate('/manage-qa')}
            >
              MANAGE Q&A
            </Button>

            <Button
              variant="outlined"
              color="primary"
              startIcon={<SettingsIcon />}
              onClick={() => navigate('/settings')}
            >
              SETTINGS
            </Button>

            <Button
              variant="outlined"
              color="primary"
              startIcon={<HelpIcon />}
              onClick={() => navigate('/help')}
            >
              HELP CENTER
            </Button>

            <ManageSubscriptionButton
              variant="outlined"
              color="primary"
              startIcon={<SubscriptionsIcon />}
            />
          </Box>
          <Box sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<RefreshIcon />}
              onClick={refreshConversations}
            >
              REFRESH CONVERSATIONS
            </Button>
          </Box>
        </Paper>

        {/* Tabs Section */}
        <Paper elevation={3} sx={{ mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            centered
          >
            <Tab label="Q&A MANAGEMENT" />
            <Tab label="CONVERSATIONS" />
            <Tab label="SETTINGS" />
          </Tabs>

          {/* Q&A Management Tab */}
          {activeTab === 0 && (
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Your Q&A Pairs
              </Typography>

              {businessData?.commonQuestions?.length > 0 ? (
                <List>
                  {businessData.commonQuestions.map((qa, index) => (
                    <React.Fragment key={index}>
                      <ListItem alignItems="flex-start">
                        <ListItemText
                          primary={qa.question}
                          secondary={qa.answer}
                        />
                      </ListItem>
                      {index < businessData.commonQuestions.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Typography variant="body1">
                  No Q&A pairs found. Add some to help the AI answer customer questions.
                </Typography>
              )}

              <Button
                variant="contained"
                color="primary"
                sx={{ mt: 2 }}
                onClick={() => navigate('/manage-qa')}
              >
                Manage Q&A Pairs
              </Button>
            </Box>
          )}

          {/* Conversations Tab */}
          {activeTab === 1 && (
            <Box sx={{ p: 3 }}>
              <DebugConversations />
            </Box>
          )}

          {/* Settings Tab */}
          {activeTab === 2 && (
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Business Settings
              </Typography>

              <Typography variant="body1" paragraph>
                Manage your business information and settings.
              </Typography>

              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate('/settings')}
              >
                Edit Settings
              </Button>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default Dashboard;
